import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appInputRestriction]'
})
export class InputRestrictionDirective {
  @Input('appInputRestriction') inputRestriction: string = '';

  constructor(private el: ElementRef, private control: NgControl) { }

  @HostListener('keypress', ['$event']) onKeyPress(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);
    let regExp: RegExp;

    switch (this.inputRestriction) {
      case 'alphabetic':
        regExp = /^[a-zA-Z\s]+$/;
        break;
      case 'alphanumeric':
        regExp = /^[a-zA-Z0-9\s]+$/;
        break;
      case 'numeric':
        regExp = /^[0-9]+$/;
        break;
      default:
        return;
    }

    if (!regExp.test(charStr)) {
      event.preventDefault();
    }
  }

  @HostListener('input', ['$event']) onInputChange(event: any) {
    const initialValue = this.el.nativeElement.value;
    let regExp: RegExp;

    switch (this.inputRestriction) {
      case 'alphabetic':
        regExp = /[^a-zA-Z\s]/g;
        break;
      case 'alphanumeric':
        regExp = /[^a-zA-Z0-9\s]/g;
        break;
      case 'numeric':
        regExp = /[^0-9]/g;
        break;
      default:
        return;
    }

    const cleaned = initialValue.replace(regExp, '');
    if (initialValue !== cleaned) {
      this.el.nativeElement.value = cleaned;
      this.control.control?.setValue(cleaned);
      event.stopPropagation();
    }
  }
}

@Directive({
  selector: '[appPinCode]'
})
export class PinCodeDirective {
  private readonly maxLength = 6;
  private readonly regExp = /[^0-9]/g;

  constructor(private el: ElementRef, private control: NgControl) { }


  @HostListener('input', ['$event']) onInputChange(event: any) {
    const initialValue = this.el.nativeElement.value;

    let cleanedValue = initialValue.replace(this.regExp, '');

    if (cleanedValue.length > this.maxLength) {
      cleanedValue = cleanedValue.slice(0, this.maxLength);
    }

    this.el.nativeElement.value = cleanedValue;

    if (initialValue !== cleanedValue) {
      this.el.nativeElement.value = cleanedValue
      this.control.control?.setValue(cleanedValue)
      event.stopPropagation();
    }
  }
}



@Directive({
  selector: '[appDecimalPositive]'
})
export class DecimalPositiveDirective {
  private readonly regExp = /[^0-9.]/g;
  private readonly maxDecimals = 2;

  constructor(private el: ElementRef) { }

  @HostListener('input', ['$event']) onInputChange(event: any) {
    const inputElement = this.el.nativeElement;
    const initialValue = inputElement.value;

    const cursorPosition = inputElement.selectionStart;

    let cleanedValue = initialValue.replace(this.regExp, '');

    const parts = cleanedValue.split('.');
    if (parts.length > 2) {
      cleanedValue = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    if (parts[1] && parts[1].length > this.maxDecimals) {
      cleanedValue = `${parts[0]}.${parts[1].slice(0, this.maxDecimals)}`;
    }

    if (initialValue !== cleanedValue) {
      inputElement.value = cleanedValue;

      const newCursorPosition = cursorPosition - (initialValue.length - cleanedValue.length);
      inputElement.setSelectionRange(newCursorPosition, newCursorPosition);

      event.stopPropagation();
    }
  }
}

@Directive({
  selector: '[appOnlyDecimalNumber]'
})
export class OnlyDecimalNumberDirective {
  private regex: RegExp = new RegExp(/^[0-9]*\.?[0-9]{0,2}$/);

  constructor(private el: ElementRef, private control: NgControl) { }

  @HostListener('keydown', ['$event']) onKeyDown(event: KeyboardEvent) {
    const key = event.key;

    if (key === '-' || key === '−' || key === '–') {
      event.preventDefault();
      return;
    }

    const inputElement = this.el.nativeElement;
    if (!this.regex.test(inputElement.value)) {
      event.preventDefault();
      return;
    }

    if (!this.regex.test(key) && !['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter', 'Escape'].includes(key)) {
      event.preventDefault();
    }
  }

  @HostListener('input', ['$event']) onInput(event: InputEvent) {
    const inputElement = this.el.nativeElement;
    const initialValue = inputElement.value;

    const cursorPosition = inputElement.selectionStart || 0;
    let cleanedValue = initialValue.replace(/[^0-9.]/g, '');
    const parts = cleanedValue.split('.');

    if (parts.length > 2) {
      cleanedValue = `${parts[0]}.${parts.slice(1).join('')}`;
    }

    if (parts[1] && parts[1].length > 2) {
      cleanedValue = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }

    if (initialValue !== cleanedValue) {
      inputElement.value = cleanedValue;

      const newCursorPosition = cursorPosition - (initialValue.length - cleanedValue.length);
      inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
    }

    this.control.control?.setValue(cleanedValue, { emitModelToViewChange: false });
    this.control.control?.markAsTouched();
    this.control.control?.updateValueAndValidity();
  }

  @HostListener('paste', ['$event']) onPaste(event: ClipboardEvent) {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    if (!this.regex.test(pastedInput)) {
      event.preventDefault();
    }
  }
}